import { useCallback, useEffect, useRef, useState } from 'react';
import { DirectionsRenderer, DirectionsService, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import environment from '../utils/envConfig';
import { ROUTE_TARGET, createLatLngObject } from '../utils/functionalHelper';

// Padding (px) kept around the outermost pin, and the tightest the map is
// allowed to auto-zoom when every point sits on the same street.
const AUTO_FIT_PADDING = 48;
const MAX_AUTO_ZOOM = 16;
// A viewport change this close to one of ours is ours, not the viewer's.
const PROGRAMMATIC_VIEWPORT_WINDOW_MS = 2000;

// A point is only usable once both coordinates have been parsed into numbers.
const hasCoordinates = (point) => Boolean(point?.lat && point?.lng);

const DirectionMapGoogle = (props) => {

    const [loaded, setLoaded] = useState(false);
    const [response, setResponse] = useState(null);
    const [map, setMap] = useState(null);
    const [requestMade, setRequestMade] = useState(false);
    const [originLocation, setOriginLocation] = useState(props.origin);

    // Where the driver is driving right now: our store while they are still
    // collecting the order, the customer once it is out for delivery. Everything
    // below this line is the same journey either way.
    const routeDestination = ((props?.routeTarget === ROUTE_TARGET.WAREHOUSE) ? (props?.warehouse) : (props?.destination));

    const containerStyle = {
        width: props?.containerWidth,
        height: props?.containerHeight
    };


    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: environment?.GOOGLE?.MAP_API
    });


    const onLoad = useCallback((map) => {
        setMap(map);
    }, []);

    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);


    const directionsCallback = (response) => {
        if (response !== null) {
            if (response.status === 'OK') {
                // console.log('Route Response: ', response);
                setResponse(response);

                const origin = (response.routes[0].legs[0].start_location);
                setOriginLocation(origin);
                const eta = (response.routes[0].legs[0].duration.text);
                props?.setEstimatedDeliveryTime(eta);
            } else {
                console.error('Directions request failed:', response);
            }
        }
    };

    useEffect(() => {
        if (map && !requestMade) {
            setRequestMade(true);
        }
    }, [map, requestMade]);

    useEffect(() => {
        if ((props.origin) && routeDestination) {
            setLoaded(true);
        }
    }, [props, routeDestination]);

    // The three points the shopper cares about: the driver, the store the order
    // is collected from, and where it is going.
    const visiblePoints = [originLocation, props?.warehouse, props?.destination].filter(hasCoordinates);
    const pointsSignature = visiblePoints.map((point) => `${point.lat},${point.lng}`).join('|');
    const pointsRef = useRef(visiblePoints);
    pointsRef.current = visiblePoints;

    const hasAutoFittedRef = useRef(false);
    const lastProgrammaticViewportAtRef = useRef(0);
    const viewerMovedTheMapRef = useRef(false);

    // Once the viewer pans or pinches, the map is theirs - Google reports our own
    // moves through the same events, hence the timestamp check.
    const onDragStart = useCallback(() => {
        viewerMovedTheMapRef.current = true;
    }, []);

    const onZoomChanged = useCallback(() => {
        if ((Date.now() - lastProgrammaticViewportAtRef.current) > PROGRAMMATIC_VIEWPORT_WINDOW_MS) {
            viewerMovedTheMapRef.current = true;
        }
    }, []);

    // Frame all three points rather than just the route, so the store stays on
    // screen while the driver is collecting the order and the delivery address
    // stays on screen once he is on his way.
    useEffect(() => {
        if (!map || viewerMovedTheMapRef.current) {
            return;
        }
        const points = pointsRef.current;
        if (!points.length) {
            return;
        }

        // Leave the map alone while everything is already in view.
        const currentBounds = map.getBounds();
        const everythingInView = (currentBounds && points.every((point) => currentBounds.contains(point)));
        if (hasAutoFittedRef.current && everythingInView) {
            return;
        }

        hasAutoFittedRef.current = true;
        lastProgrammaticViewportAtRef.current = Date.now();

        if (points.length === 1) {
            map.setCenter(points[0]);
            map.setZoom(MAX_AUTO_ZOOM);
            return;
        }

        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((point) => bounds.extend(point));
        map.fitBounds(bounds, AUTO_FIT_PADDING);
        window.google.maps.event.addListenerOnce(map, 'idle', () => {
            // Two pins a street apart would otherwise fit at max zoom.
            if (map.getZoom() > MAX_AUTO_ZOOM) {
                lastProgrammaticViewportAtRef.current = Date.now();
                map.setZoom(MAX_AUTO_ZOOM);
            }
        });
        // Only the points themselves matter here, not every prop identity change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, pointsSignature, props?.routeTarget]);

    return isLoaded ? (
        <GoogleMap
            center={createLatLngObject((props?.destination) || '23.8382728,78.7475463')}
            mapContainerStyle={containerStyle}
            zoom={12}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onDragStart={onDragStart}
            onZoomChanged={onZoomChanged}
            options={{ streetViewControl: false, mapTypeControl: false }}
        >
            {/* Our store - the pickup point the driver collects the order from. */}
            {(props?.warehouse) && (
                <Marker
                    position={props?.warehouse}
                    title={(props?.warehouseTitle) || 'Our store'}
                    icon={{
                        url: '/images/green_mark.png',
                        scaledSize: new window.google.maps.Size(40, 40),
                    }}
                />
            )}

            {originLocation && (
                <Marker
                    position={originLocation}
                    zIndex={3}
                    icon={{
                        url: '/images/shipping_truck.png',
                        scaledSize: new window.google.maps.Size(50, 50),
                        // anchor: new window.google.maps.Point(20, 30)
                        // rotation: calculateBearing(originLocation, destinationLocation)
                    }}
                />
            )}

            {/* The delivery address stays pinned even while the route being drawn
                ends at our store, so the shopper never loses sight of it. */}
            {(props?.destination?.lat && props?.destination?.lng) && (
                <Marker
                    position={props?.destination}
                    icon={{
                        url: '/images/red_mark.png',
                        scaledSize: new window.google.maps.Size(50, 50),
                    }}
                />
            )}


            {(!response) && requestMade && loaded && (props?.assigneeGeoCoordinatesAvailable) && (
                <DirectionsService
                    options={{
                        origin: (props.origin),
                        destination: routeDestination,
                        travelMode: 'DRIVING',
                        provideRouteAlternatives: false,
                    }}
                    callback={directionsCallback}
                />
            )}

            {response && (
                <DirectionsRenderer
                    options={{
                        directions: response,
                        suppressMarkers: true,
                        // We frame the map over all three pins ourselves - fitting the
                        // route alone would push the third one off screen.
                        preserveViewport: true,
                    }}
                />
            )}
        </GoogleMap>
    ) : (
        <>Map could not be loaded</>
    );
};

export default DirectionMapGoogle;
